import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { divider, hstack, stack } from '@flipster/styled-system/patterns';
import { formatDatetime } from '@flipster/utils/datetime';

import { communityFeedBunja } from '../bunja';
import { SymbolLeaderboard } from './symbol-leaderboard';
import { SymbolOpenPositionCarousel } from './symbol-open-position-carousel';
import { SymbolOpenPositionPercent } from './symbol-open-position-percent';

const InfoAlert = {
  title: <FormattedMessage id='Autogen_common.r251126.s_Top_100_traders_open_positions' />,
  message: (
    <>
      <FormattedMessage id='Autogen_common.r251126.s_Long_vs_short_ratio_for_each_token_based_on_open_positions_from_the_top_100_traders_by_P_L' />
      <br />
      <FormattedMessage id='Autogen_common.r251126.s_Calculated_using_position_size_and_updated_every_10_minutes' />
      <br />
      <FormattedMessage id='Autogen_common.r251126.s_Calculated_using_position_size_and_updated_every_10_minutes' />
    </>
  ),
  confirmText: <FormattedMessage id='Common.r250401.Done' />,
};

interface SymbolGeneratedContentsProps {
  css?: Styles;
}
export const SymbolGeneratedContents = ({ css: cssProps }: SymbolGeneratedContentsProps) => {
  const { symbol, symbolLeaderboardAtom, symbolOpenPositionPctAtom, symbolOpenPositionsAtom } =
    useBunja(communityFeedBunja);
  const symbolLeaderboard = useAtomValue(symbolLeaderboardAtom);
  const symbolOpenPositionPct = useAtomValue(symbolOpenPositionPctAtom);
  const symbolOpenPositions = useAtomValue(symbolOpenPositionsAtom);

  const showSymbolLeaderboard = symbolLeaderboard?.rankings != null && symbolLeaderboard.rankings.length > 0;
  const showOpenPositionPercent = symbolOpenPositionPct != null;
  const showOpenPositionsCarousel = symbolOpenPositions.length > 0;

  if (!symbol || !(showSymbolLeaderboard || showOpenPositionPercent || showOpenPositionsCarousel)) return null;
  return (
    <div className={css(stack.raw({ gap: 24 }), cssProps)}>
      {showSymbolLeaderboard && <SymbolLeaderboard symbol={symbol} rankings={symbolLeaderboard.rankings} />}
      <hr
        className={divider({
          color: 'border.default.level-1',
          borderBottomWidth: 0,
          _first: { display: 'none' },
          _last: { display: 'none' },
        })}
      />
      {(showOpenPositionPercent || showOpenPositionsCarousel) && (
        <div className={stack({ gap: { base: 16, smallDesktop: 12 } })}>
          <div>
            <SymbolOpenPositionContentsHeader />
            {symbolOpenPositionPct && (
              <SymbolOpenPositionContentsTimestamp updatedAt={symbolOpenPositionPct.updatedAt} />
            )}
          </div>
          <div className={stack({ gap: 16 })}>
            {showOpenPositionPercent && (
              <SymbolOpenPositionPercent openPositionStat={symbolOpenPositionPct} css={{ mt: 8 }} />
            )}
            {showOpenPositionsCarousel && <SymbolOpenPositionCarousel openPositions={symbolOpenPositions} />}
          </div>
        </div>
      )}
    </div>
  );
};

const SymbolOpenPositionContentsHeader = () => {
  return (
    <div className={hstack()}>
      <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Top_100_traders_open_positions' />
      </p>
      <IconButton
        hierarchy='quaternary'
        size='small'
        icon={<InformationCircleIcon />}
        onClick={() =>
          dialog.open({
            title: { label: InfoAlert.title },
            content: InfoAlert.message,
            buttons: {
              primary: <Dialog.Button hierarchy='primary' label={InfoAlert.confirmText} />,
            },
          })
        }
      />
    </div>
  );
};

interface SymbolOpenPositionContentsTimestampProps {
  updatedAt: string;
}
const SymbolOpenPositionContentsTimestamp = ({ updatedAt }: SymbolOpenPositionContentsTimestampProps) => {
  return (
    <p
      className={css({
        textStyle: 'Text/S/Regular',
        color: 'content.default.level-1',
      })}
    >
      <FormattedMessage
        id='Autogen_common.r251126.s_Updated_every_10_minutes_Last_update_formatDatetime'
        values={{
          formatDatetime: formatDatetime(updatedAt, { format: "MMM dd 'at' HH:mm zz", isUTC: true }),
        }}
      />
    </p>
  );
};
